/**
 * FanPlotsGrid — 6-up grid of macro-forecast fan charts, one per variable.
 *
 * Data for the whole grid comes from a single
 * `/api/mars/live/fan/vintages/<date>/multi` call, so one request fans out
 * to six charts.
 */
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import PlotlyChart from '@/lib/plotly/PlotlyChart';
import ChartCard from '@/components/ChartCard';
import ChartEmpty from '@/components/ChartEmpty';
import QueryState from '@/components/QueryState';
import { FAN_VARIABLES, fetchVintageMulti, findPrevMonth, mlKeys } from '../api';
import {
  FAN_CHARTS,
  buildFanLayout,
  buildFanTraces,
  mergePreviousMonth,
  processVintageData,
} from '@/lib/plotly/fanChart';
import type { FanRecord } from '../types';

export interface FanPlotsGridProps {
  vintageDate: string;
  vintages: string[];
}

function useVintageMulti(vintageDate: string | null): {
  data: Record<string, FanRecord[]> | undefined;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
} {
  const enabled = vintageDate !== null;
  const query = useQuery({
    queryKey: mlKeys.vintageMulti(vintageDate ?? ''),
    queryFn: () => fetchVintageMulti(vintageDate ?? ''),
    enabled,
    staleTime: 60_000,
  });
  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
  };
}

export default function FanPlotsGrid({ vintageDate, vintages }: FanPlotsGridProps): JSX.Element {
  const current = useVintageMulti(vintageDate);

  const prevDate = useMemo(
    () => (vintageDate ? findPrevMonth(vintages, vintageDate) : null),
    [vintageDate, vintages],
  );
  const prev = useVintageMulti(prevDate);

  // Size the axis from the rendered vintage's inner p16/p84 band + median +
  // actual (via `buildFanLayout` → `computeYRange`), matching the legacy
  // `mars-fan-plots.js` axis and the mars-forecasts MacroEconomicTab. The
  // global `/y_ranges` endpoint is intentionally NOT used here: it widens
  // the range across all vintages' p5/p95 tails, ballooning 10 Year Yield
  // to ~-5..15 and BAA Spread to a near-empty 0..6 that dwarfs the actual
  // series.
  const charts = useMemo(() => {
    if (!current.data) return [];
    return FAN_CHARTS.map((def) => {
      const rawCurrent = current.data![def.variable] ?? [];
      const rawPrev = prev.data ? (prev.data[def.variable] ?? null) : null;
      const merged = mergePreviousMonth(rawCurrent, rawPrev);
      const processed = processVintageData(merged);
      const traces = buildFanTraces(processed, def);
      // Anchor every cursor line to the SELECTED vintage. Without it each
      // chart falls back to its own data-derived forecast start (GDP
      // 05-01, CPI 07-01, ...), so the six lines disagree with the slider.
      const layout = buildFanLayout(def, processed, null, vintageDate);
      return { def, traces, layout };
    });
  }, [current.data, prev.data, vintageDate]);

  return (
    <>
      {/* No section heading — the slider's "Forecasts as of" label already
       * introduces the grid, and the page must fit one laptop screen. */}
      {/* 3 columns at >=1024px; collapses to 2 at md and 1 at sm. */}
      <QueryState
        query={{
          isLoading: current.isLoading,
          isError: current.isError,
          data: current.data,
          error: current.error,
        }}
        loadingLabel="Loading"
        errorLabel={(error) =>
          `Failed to load macro forecasts: ${error instanceof Error ? error.message : 'unknown error'}`
        }
        minHeight={240}
      >
        {() => (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {FAN_VARIABLES.map((variable) => {
              const entry = charts.find((c) => c.def.variable === variable);
              return (
                // ~380x240 per cell — tall enough that the actual series'
                // movements stay visible; 180 was too flat.
                <ChartCard key={variable} height={240}>
                  {!entry ? (
                    <ChartEmpty message="No forecast data" />
                  ) : (
                    <PlotlyChart data={entry.traces} layout={entry.layout} />
                  )}
                </ChartCard>
              );
            })}
          </div>
        )}
      </QueryState>
    </>
  );
}
